/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Component imports
import { Card, Col, Empty, Grid, PageHeader, Row, Spin, Tabs, Typography } from 'antd';
import ActivitiesView from '../components/UI/ActivitiesView';
import AudienceTable from '../components/Tables/AudienceTable';
import AutomatedAnswersTable from '../components/Tables/AutomatedAnswersTable';
import BackupsTable from '../components/Tables/BackupsTable';
import BotLanguageSelect from '../components/UI/BotLanguageSelect';
import BroadcastsTable from '../components/Tables/BroadcastsTable';
import DefaultFallbacksForm from '../components/Forms/DefaultFallbacksForm';
import Permissioned from '../components/Permissioned';
import PowerupsForm from '../components/Forms/PowerupsForm';
import BotTestButtons from '../components/UI/BotTestButtons';
import NlpPipelinesTable from '../components/Tables/NlpPipelinesTable';
import SecondaryLanguageAlert from '../components/UI/SecondaryLanguageAlert';
import BotDeploymentsScreen from './BotDeploymentsScreen';
import BotSettingsForm from '../components/Forms/BotSettingsForm';
import PrivacySettingsForm from '../components/Forms/PrivacySettingsForm';
import TestSetsEditor from '../components/UI/TestSetsEditor';
import KnowledgeBaseSourcesTable from '../components/Tables/KnowledgeBaseSourcesTable';
import BotPluginsForm from '../components/Forms/BotPluginsForm';
import NlpFlowsTable from '../components/Tables/NlpFlowsTable';
import NlpConfigurationForm from '../components/Forms/NlpConfigurationForm';
import TranslationsScreen from './TranslationsScreen';
import NlpKeywordsView from '../components/UI/NlpKeywordsView';
import StaticVariablesView from '../components/UI/StaticVariablesView';
import DocumentTitle from '../components/DocumentTitle';

// Misc imports
import { PERMISSIONS_GROUP_ADMIN, PERMISSIONS_GROUP_VIEWER, POWERUPS } from '../constants/main';
import { SIDEBAR_MENU_ITEMS } from '../constants/navigation';
import { updateRecentBots } from '../store/bots/botsActions';
import { useFetchBotQuery } from '../store/bots/botsApi';
import { useBotLanguage } from '../utils/hooks/useBotLanguage';
import { isEmptyArray } from '../utils/is/isEmptyArray';
import { useFetchBotDeploymentsQuery } from '../store/botDeployments/botDeploymentsApi';
import { useFetchStoryVariablesQuery } from '../store/story/storyApi';
import useUserPermissions from '../utils/hooks/useUserPermissions';
import is from '../utils/is';

// Constant declarations
const { Text } = Typography;
const { useBreakpoint } = Grid;

const BotScreen = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { tenantId, orgId } = useParams();
  const userPermissions = useUserPermissions(orgId, tenantId);
  const searchParams = new URLSearchParams(location.search);
  const screens = useBreakpoint();
  const isMobileOrTabletSize = (screens.xs || screens.sm || screens.md || screens.lg) && !screens.xl;
  const { currentBotLanguage, changeBotLanguage } = useBotLanguage();
  const currentSidebarMenuItem = Object.values(SIDEBAR_MENU_ITEMS).find(sidebarItem => sidebarItem.key === searchParams.get('view'));

  const { recentBots } = useSelector((state) => state.bots);
  const { data: bot, isFetching: isFetchingBot } = useFetchBotQuery({ orgId, tenantId }, { skip: !orgId || !tenantId });
  const { data: botDeployments } = useFetchBotDeploymentsQuery({ orgId, tenantId }, { skip: !tenantId });
  useFetchStoryVariablesQuery({ orgId, tenantId }); // Fetch story variables to keep them up to date

  // Hide the powerups settings tab (we want it to be accessible only via URL)
  useEffect(() => {
    const powerupsSettingsTab = document.querySelector('[data-node-key="powerups-settings"]');
    if (powerupsSettingsTab) {
      powerupsSettingsTab.classList.add('d-none');
    }
  }, [location]);

  useEffect(() => {
    if (bot && !isFetchingBot) {
      // Update the URL with the current content language
      changeBotLanguage(currentBotLanguage);

      // Update the "recent bots" list shown in the bot selection panel
      dispatch(updateRecentBots([bot, ...recentBots]));
    }
  }, [bot]);

  useEffect(() => {
    // Whenever we change bot, we need to reset the current bot language to the primary one
    changeBotLanguage(bot?.settings?.languages?.primary);

    // Redirect to first available page if current view is invalid
    if (bot && !currentSidebarMenuItem) {
      navigate(location.pathname, { replace: true });
    }
  }, [bot?.id]);

  const isBotDeploymentsInitialScreen = useMemo(() => {
    return (!!botDeployments?.items?.find((deployment) => deployment.isPreview === true) && botDeployments?.items?.length === 1) || isEmptyArray(botDeployments?.items);
  }, [botDeployments?.items]);

  const changeTab = activeKey => {
    const urlSearchParams = new URLSearchParams(location.search);
    const nextUrlSearchParams = new URLSearchParams();
    nextUrlSearchParams.set('view', urlSearchParams.get('view'));
    nextUrlSearchParams.set('tab', activeKey);

    // If we are in the behavior view, we need to keep the contentLang parameter
    if (nextUrlSearchParams.get('view') === 'behavior') {
      nextUrlSearchParams.set('contentLang', currentBotLanguage);
    }

    navigate({
      pathname: location.pathname,
      search: nextUrlSearchParams.toString()
    }, { replace: true });
  };

  const fallbackToFirstAvailableTab = (availableTabs, currentTab) => {
    if (!Object.values(availableTabs).includes(currentTab)) {
      currentTab = availableTabs[Object.keys(availableTabs)[0]];
      if (!(searchParams.get('tab') === availableTabs[Object.keys(availableTabs)[0]])) {
        setTimeout(() => {
          changeTab(availableTabs[Object.keys(availableTabs)[0]]);
        });
      }
    }
  };

  const renderPageHeader = () => {
    return currentSidebarMenuItem ? (
      <PageHeader
        title={(
          currentSidebarMenuItem.key === SIDEBAR_MENU_ITEMS.BEHAVIOR.key ? (
            <BotLanguageSelect
              size="large"
              selectStyle={{ width: 170 }}
            />
          ) : null
        )}
        extra={[
          !isMobileOrTabletSize ? [<BotTestButtons disabled={isFetchingBot && !bot} />] : []
        ]}
      />
    ) : null;
  };

  const renderCurrentPowerup = () => {
    const currentPowerup = searchParams.get('view');
    let currentTab = searchParams.get('tab');
    let TABS = [];

    switch (currentPowerup) {
      case SIDEBAR_MENU_ITEMS.BEHAVIOR.key:
        TABS = {
          ACTIVITIES: 'flows',
          AUTOMATED_ANSWERS: 'automated-answers',
          FALLBACKS: 'fallbacks',
          STATIC_VARIABLES: 'static-variables',
          TRANSLATIONS: 'translations'
        };

        // Remove any tabs that should not be available (eg. if the respective powerup does not exist)
        if (!bot.powerups.includes(POWERUPS.FLOWS.key)) {
          delete TABS.ACTIVITIES;
        }
        if (!bot.powerups.includes(POWERUPS.AUTOMATED_ANSWERS.key)) {
          delete TABS.AUTOMATED_ANSWERS ;
        }

        // Fallback to the correct tab if needed
        fallbackToFirstAvailableTab(TABS, currentTab);

        return (
          <Fragment>
            <SecondaryLanguageAlert showLanguageSwitchPrompt className="mb-4" />
            <Tabs
              activeKey={currentTab}
              // Moved t noTabClick instead onChange in order to handle the cases that we are in a nested view
              // and click on the same tab, in which case we need to send the user to the landing tab view
              onTabClick={(k) => changeTab(k)}
              destroyInactiveTabPane
              animated={false}
              tabBarStyle={{ display: 'none' }} // Used to be {{ display: searchParams.has('activity') ? 'none' : 'flex' }}
              items={[
                TABS.ACTIVITIES && {
                  key: TABS.ACTIVITIES,
                  label: t('Flows'),
                  children: (
                    <Fragment>
                      <DocumentTitle title={t('Flows')} />
                      <ActivitiesView />
                    </Fragment>
                  )
                },

                TABS.AUTOMATED_ANSWERS && {
                  key: TABS.AUTOMATED_ANSWERS,
                  label: t('Automated Answers'),
                  children: (
                    <Fragment>
                      <DocumentTitle title={t('Automated Answers')} />
                      <AutomatedAnswersTable />
                    </Fragment>
                  )
                },

                TABS.FALLBACKS && {
                  key: TABS.FALLBACKS,
                  label: t('Default Flow'),
                  children: (
                    <Fragment>
                      <DocumentTitle title={t('Default Flow')} />
                      <DefaultFallbacksForm />
                    </Fragment>
                  )
                },

                TABS.STATIC_VARIABLES && {
                  key: TABS.STATIC_VARIABLES,
                  label: t('Variables'),
                  children: (
                    <Fragment>
                      <DocumentTitle title={t('Variables')} />
                      <StaticVariablesView />
                    </Fragment>
                  )
                },

                TABS.TRANSLATIONS && {
                  key: TABS.TRANSLATIONS,
                  label: t('Translations'),
                  children: (
                    <Fragment>
                      <DocumentTitle title={t('Translations')} />
                      <TranslationsScreen />
                    </Fragment>
                  )
                }
              ]}
            />
          </Fragment>
        );

      case SIDEBAR_MENU_ITEMS.SETTINGS.key:
        TABS = {
          SETTINGS: 'settings',
          WEB_CHAT_SETTINGS: 'web-chat-settings',
          POWERUPS_SETTINGS: 'powerups-settings',
          KNOWLEDGE_BASE_SOURCES: 'knowledge-base-sources',
          PRIVACY_SETTINGS: 'privacy-settings'
        };

        // Remove any tabs that should not be available (eg. if the respective powerup does not exist)
        if (!bot.powerups.includes(POWERUPS.SETTINGS.key)) {
          delete TABS.SETTINGS;
        }

        if (!userPermissions.some(permission => PERMISSIONS_GROUP_ADMIN.includes(permission))) {
          delete TABS.POWERUPS_SETTINGS;
        }

        // Fallback to the correct tab if needed
        fallbackToFirstAvailableTab(TABS, currentTab);

        return (
          <Tabs
            activeKey={currentTab}
            onTabClick={(tab) => changeTab(tab)}
            destroyInactiveTabPane
            animated={false}
            tabBarStyle={{ display: 'none' }}
            items={[
              TABS.SETTINGS && {
                key: TABS.SETTINGS,
                label: t('Configuration'),
                children: (
                  <Fragment>
                    <DocumentTitle title={t('Configuration')} />
                    <Permissioned
                      allowedPermissions={[POWERUPS.SETTINGS.key]}
                      currentPermissions={bot.powerups}
                      fallbackComponent={null}
                    >
                      <BotSettingsForm />
                    </Permissioned>
                  </Fragment>
                )
              },

              TABS.POWERUPS_SETTINGS && {
                key: TABS.POWERUPS_SETTINGS,
                label: t('Powerups'),
                children: (
                  <Fragment>
                    <DocumentTitle title={t('Powerups')} />
                    <Permissioned
                      allowedPermissions={[POWERUPS.SETTINGS.key]}
                      currentPermissions={bot.powerups}
                      fallbackComponent={null}
                    >
                      <PowerupsForm/>
                    </Permissioned>
                  </Fragment>
                )
              },

              TABS.KNOWLEDGE_BASE_SOURCES && {
                key: TABS.KNOWLEDGE_BASE_SOURCES,
                label: t('Knowledge Bases'),
                children: (
                  <Fragment>
                    <DocumentTitle title={t('Knowledge Bases')} />
                    <KnowledgeBaseSourcesTable />
                  </Fragment>
                )
              },

              TABS.PRIVACY_SETTINGS && {
                key: TABS.PRIVACY_SETTINGS,
                label: t('Privacy'),
                children: (
                  <Fragment>
                    <DocumentTitle title={t('Privacy')} />
                    <PrivacySettingsForm />
                  </Fragment>
                )
              }
            ]
            }
          />
        );

      case SIDEBAR_MENU_ITEMS.DEPLOYMENTS.key:
        return (
          <Fragment>
            <DocumentTitle title={t('Deployments')} />
            <BotDeploymentsScreen
              isInitialScreen={isBotDeploymentsInitialScreen}
            />
          </Fragment>
        );

      case SIDEBAR_MENU_ITEMS.BROADCASTS.key:
        TABS = {
          BROADCASTS: 'broadcasts',
          AUDIENCE: 'audience'
        };

        // Remove any tabs that should not be available (eg. if the respective powerup does not exist)
        if (!userPermissions.some(permission => PERMISSIONS_GROUP_ADMIN.includes(permission))) {
          delete TABS.AUDIENCE;
        }

        // Fallback to the correct tab if needed
        fallbackToFirstAvailableTab(TABS, currentTab);

        return (
          <Tabs
            activeKey={currentTab}
            onTabClick={(k) => changeTab(k)}
            destroyInactiveTabPane
            animated={false}
            tabBarStyle={{ display: 'none' }}
            items={[
              TABS.BROADCASTS && {
                key: TABS.BROADCASTS,
                label: t('Broadcasts'),
                children: (
                  <Fragment>
                    <DocumentTitle title={t('Broadcasts')} />
                    <BroadcastsTable />
                  </Fragment>
                )
              },

              TABS.AUDIENCE && {
                key: TABS.AUDIENCE,
                label: t('Audiences'),
                children: (
                  <Fragment>
                    <DocumentTitle title={t('Audiences')} />
                    <AudienceTable />
                  </Fragment>
                )
              }
            ]}
          />
        );

      case SIDEBAR_MENU_ITEMS.ARTIFICIAL_INTELLIGENCE.key:
        TABS = {
          NLP_CONFIGURATION: 'nlp-configuration',
          NLP_SYSTEMS: 'nlp-systems',
          NLP_FLOWS: 'nlp-flows',
          TEST_NLPS: 'test-nlps',
          NLP_KEYWORDS: 'nlp-keywords'
        };

        // Remove any tabs that should not be available (eg. if the respective powerup does not exist)
        if (!bot.powerups.includes(POWERUPS.AUTOMATED_ANSWERS.key) || !userPermissions.some(permission => PERMISSIONS_GROUP_ADMIN.includes(permission))) {
          delete TABS.NLP_CONFIGURATION;
          delete TABS.NLP_SYSTEMS;
          delete TABS.NLP_FLOWS;
          delete TABS.NLP_KEYWORDS;
        }

        // Fallback to the correct tab if needed
        fallbackToFirstAvailableTab(TABS, currentTab);

        return (
          <Tabs
            activeKey={currentTab}
            onTabClick={changeTab}
            destroyInactiveTabPane
            animated={false}
            tabBarStyle={{ display: 'none' }}
            items={[
              TABS.NLP_CONFIGURATION && {
                key: TABS.NLP_CONFIGURATION,
                label: t('NLP Configuration'),
                children: (
                  <Fragment>
                    <DocumentTitle title={t('NLP Configuration')} />
                    <NlpConfigurationForm />
                  </Fragment>
                )
              },

              TABS.NLP_SYSTEMS && {
                key: TABS.NLP_SYSTEMS,
                label: t('NLP Systems'),
                children: (
                  <Fragment>
                    <DocumentTitle title={t('NLP Systems')} />
                    <NlpPipelinesTable />
                  </Fragment>
                )
              },

              TABS.NLP_FLOWS && {
                key: TABS.NLP_FLOWS,
                label: t('NLP Flows'),
                children: (
                  <Fragment>
                    <DocumentTitle title={t('NLP Flows')} />
                    <NlpFlowsTable />
                  </Fragment>
                )
              },

              TABS.TEST_NLPS && {
                key: TABS.TEST_NLPS,
                label: t('NLP Testing'),
                children: (
                  <Fragment>
                    <DocumentTitle title={t('NLP Testing')} />
                    <TestSetsEditor />
                  </Fragment>
                )
              },

              TABS.NLP_KEYWORDS && {
                key: TABS.NLP_KEYWORDS,
                label: t('Priority Keywords'),
                children: (
                  <Fragment>
                    <DocumentTitle title={t('Priority Keywords')} />
                    <NlpKeywordsView />
                  </Fragment>
                )
              }
            ]}
          />
        );

      case SIDEBAR_MENU_ITEMS.PLUGINS.key:
        return (
          <Fragment>
            <DocumentTitle title={t('Plugins')} />
            <BotPluginsForm />
          </Fragment>
        );

      case SIDEBAR_MENU_ITEMS.BACKUPS.key:
        return (
          <Fragment>
            <DocumentTitle title={t('Backups')} />
            <Permissioned
              allowedPermissions={[POWERUPS.SETTINGS.key]}
              currentPermissions={bot.powerups}
              fallbackComponent={null}
            >
              <BackupsTable/>
            </Permissioned>
          </Fragment>
        );

      default:
        return null;
    }
  };

  const renderPageContents = () => {
    if (bot && bot.powerups) {
      if (is.emptyArray(bot.powerups)) {
        return (
          <Card>
            <Empty
              description={<Text type="secondary">{t('This agent has no contents. Please contact your administrator.')}</Text>}
            />
          </Card>
        );
      } else {
        return (
          <Fragment>
            {renderPageHeader()}
            {renderCurrentPowerup()}
          </Fragment>
        );
      }
    }

    if (isFetchingBot && !bot) {
      return (
        <Row className="w-100">
          <Col flex="auto" className="text-center h-100">
            <Spin className="mt-6"/>
          </Col>
        </Row>
      );
    }

    // Else
    return (
      <Card>
        <Empty
          description={<Text type="secondary">{t('Agent information is not available. Please refresh the page to try again.')}</Text>}
        />
      </Card>
    );
  };

  return (
    <Fragment>
      <Permissioned
        allowedPermissions={PERMISSIONS_GROUP_VIEWER}
        currentPermissions={userPermissions}
      >
        {renderPageContents()}
      </Permissioned>
    </Fragment>
  );
};

export default BotScreen;
